import { computed, onMounted, onBeforeUnmount, watch, useHost, type Ref } from "vue";
import type { EditorProps } from "./editor.types";
import { useSharedState } from "composables/useSharedState";
import { editorClasses, editorFigureClasses } from "./Editor.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { createEventDispatcher } from "@/runtime/shared/events";
import { createMutableState, initMonaco, disposeEditorResources, setupEditorWatchers } from "./utils/editorState";

export interface UseEditorOptions {
  props: Readonly<EditorProps>;
  containerRef: Ref<HTMLElement | null>;
  rootRef: Ref<HTMLElement | null>;
}

export const useEditor = (options: UseEditorOptions) => {
  const { props, containerRef, rootRef } = options;
  const state = useSharedState(props);
  const host = useHost();
  const dispatchEvent = createEventDispatcher(host);
  const ms = createMutableState();

  const figureClasses = computed(() => mergeClassProp(editorFigureClasses, props.class));
  const bodyClasses = computed(() => editorClasses());
  const resolvedLanguage = computed(() => state.language?.value ?? props.language ?? "javascript");

  const handleHeaderCopy = () => {
    if (ms.editor) navigator.clipboard.writeText(ms.editor.getValue()).catch(() => {});
  };

  if (host) host.addEventListener("editor-copy", handleHeaderCopy);

  onMounted(async () => {
    if (host && !host.querySelector("e-editor-header")) {
      ms.autoHeader = document.createElement("e-editor-header");
      ms.autoHeader.setAttribute("slot", "header");
      ms.autoHeader.setAttribute("language", resolvedLanguage.value);
      host.prepend(ms.autoHeader);
    }

    const root = rootRef.value?.getRootNode();
    if (!(root instanceof ShadowRoot)) {
      console.error("MonacoCE: Shadow root not found.", root);
      return;
    }
    await initMonaco(ms, root, containerRef, props, state, dispatchEvent);
  });

  onBeforeUnmount(() => {
    disposeEditorResources(ms, host, handleHeaderCopy);
  });

  setupEditorWatchers(ms, state, containerRef, resolvedLanguage, watch);

  const formName = computed(() => state.name?.value ?? null);
  const formValue = computed(() => state.value?.value ?? "");

  return { figureClasses, bodyClasses, state, formName, formValue };
};

export { EDITOR_PAD } from "./utils/editorState";
